import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { Chart, Plugin } from "chart.js";
import { color } from "chart.js/helpers";

import { numero, pct } from "../bsc-formato";
import { COLOR_BSC, GraficaBsc } from "./grafica-bsc";

export interface SerieAreaQBsc {
  nombre: string;
  real: (number | null)[];
}

const TRIMESTRES = ["Q1", "Q2", "Q3", "Q4"];

@Component({
  selector: "app-cumplimiento-area-q-bsc",
  standalone: true,
  template: `
    <div class="bsc-acumulado" style="height: 300px">
      <canvas #lienzo role="img" [attr.aria-label]="textoAccesible"></canvas>
    </div>
    <p class="bsc-grafica-leyenda">Línea punteada · Objetivo Solser {{ textoObjetivo }}</p>
  `,
  styleUrls: ["./grafica-bsc.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CumplimientoAreaQBscComponent extends GraficaBsc {
  @Input() areas: SerieAreaQBsc[] = [];
  @Input() objetivo: number | null = null;

  get textoObjetivo(): string {
    return pct(this.objetivo, "sin dato");
  }

  get textoAccesible(): string {
    const detalle = this.areas.map((area) => `${area.nombre}: ${TRIMESTRES.map(
      (q, i) => `${q} ${pct(numero(area.real[i]), "sin dato")}`,
    ).join(", ")}`).join("; ");
    return `Cumplimiento por área y trimestre. ${detalle}. Objetivo Solser ${this.textoObjetivo}.`;
  }

  protected arma(lienzo: HTMLCanvasElement): Chart | null {
    if (this.areas.length === 0) {
      return null;
    }
    const acento = this.color(COLOR_BSC.objetivo);
    const suave = this.color(COLOR_BSC.suave);
    const objetivo = numero(this.objetivo);
    const series = TRIMESTRES.map((q, i) => ({
      label: q,
      data: this.areas.map((area) => numero(area.real[i])),
      backgroundColor: color(acento).lighten((3 - i) * 0.22).rgbString(),
      borderRadius: 4,
      maxBarThickness: 32,
      skipNull: true,
    }));
    const maximo = Math.max(1, objetivo ?? 0, ...series.flatMap((serie) => serie.data.map((v) => v ?? 0)));
    const linea: Plugin<"bar"> = {
      id: "bscObjetivoTrimestral",
      afterDatasetsDraw: ({ ctx, chartArea, scales }) => {
        if (objetivo == null) {
          return;
        }
        const y = scales["y"].getPixelForValue(objetivo);
        ctx.save();
        ctx.strokeStyle = acento;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.restore();
      },
    };
    return new Chart<"bar", (number | null)[], string>(lienzo, {
      type: "bar",
      data: { labels: this.areas.map((area) => area.nombre), datasets: series },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            ticks: { color: suave, font: { size: 11 }, autoSkip: false },
            grid: { display: false },
            border: { display: false },
          },
          y: {
            min: 0,
            max: maximo,
            ticks: { color: suave, callback: (v) => pct(Number(v)), maxTicksLimit: 6 },
            grid: { color: this.color(COLOR_BSC.rejilla) },
            border: { display: false },
          },
        },
        plugins: {
          legend: { position: "bottom", labels: { color: suave }, onClick: () => undefined },
          tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${pct(c.raw as number | null, "sin dato")}` } },
        },
      },
      plugins: [linea],
    });
  }
}
